<script setup lang="ts">
import { computed } from 'vue'
import { WdsHeading, WdsIcon, WdsIconNameEnum, WdsModal, WdsStack } from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { UpdateWorksomeIntelligenceConsentMutation } from '@/components/LLM/graphql/mutations'
import { NetworkError, Notification } from '@/utils/notification'
import enums from '@/utils/enum'
import { useAiConsentStore } from '@/state/useAiConsentStore'

const aiConsentStore = useAiConsentStore()
const show = defineModel<boolean>('show', { required: true })

const emit = defineEmits<{
  'ai-terms-agreement-agreed': []
  'ai-terms-agreement-declined': []
  'ai-terms-agreement-error': []
}>()

const { mutate: updateWorksomeIntelligenceConsentMutation } = useMutation(
  UpdateWorksomeIntelligenceConsentMutation,
  {
    clientId: 'publicApi',
  },
)

const aiHelpArticleUrl =
  'https://help.worksome.dk/en/articles/12107614-worksome-intelligence-smarter-simpler-and-built-for-you'
const legalCenterUrl = 'https://www.worksome.com/legal-center'

const dataProcessingText = computed(() =>
  $t('Data may be processed by third parties. :instruction unless you have proper consent.', {
    instruction: `<strong>${$t('Don’t submit sensitive information')}</strong>`,
  }),
)

const learnMoreText = computed(() =>
  $t('Learn more about :link.', {
    link: `<a href="${aiHelpArticleUrl}" target="_blank">${$t('how Worksome uses AI')}</a>`,
  }),
)

const consentAcceptanceText = computed(() =>
  $t('By clicking :cta, you accept the :terms.', {
    cta: `<strong>${$t('I agree')}</strong>`,
    terms: `<a href="${legalCenterUrl}" target="_blank">${$t('Terms of Service')}</a>`,
  }),
)

const handleAiConsent = async (consent: boolean) => {
  try {
    await updateWorksomeIntelligenceConsentMutation({
      input: {
        consent,
      },
    })
  } catch (e: any) {
    show.value = false
    emit('ai-terms-agreement-error')

    // Simplify error handling
    if (e.graphQLErrors?.length > 0) {
      new Notification({
        msg: e.graphQLErrors[0].message,
        type: enums.notification.types.ERROR,
        severity: enums.notification.severity.CRITICAL,
        config: { timeout: 5000 },
      }).dispatch()
    } else {
      new NetworkError({
        severity: enums.notification.severity.CRITICAL,
        ex: e,
      }).dispatch()
    }

    return
  }

  // Close modal
  show.value = false

  // Emit events
  if (consent) {
    aiConsentStore.grantConsent()
    emit('ai-terms-agreement-agreed')
  } else {
    emit('ai-terms-agreement-declined')
  }
}
</script>

<template>
  <WdsModal
    :show="show"
    md
    class="ai-terms-modal !tw-overflow-y-hidden"
    @cancel="handleAiConsent(false)"
  >
    <template #header>
      <WdsStack gap-sm align-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" />
        <WdsHeading class="tw-my-0" type="title2">
          {{ $t('Worksome Intelligence') }}
        </WdsHeading>
      </WdsStack>
    </template>

    <div class="tw-max-h-[70vh] tw-overflow-y-auto">
      <slot name="terms">
        <p>
          <strong>{{ $t('Experience Worksome Intelligence') }}</strong>
        </p>
        <p>
          {{
            $t(
              'We’re excited to introduce Worksome Intelligence, our AI-powered feature designed to enhance your workflow.',
            )
          }}
          {{ $t('To ensure you get the most from it, please keep in mind:') }}
        </p>
        <ul>
          <li>
            {{
              $t('Worksome Intelligence may occasionally produce inaccurate or incomplete results.')
            }}
          </li>
          <li>
            {{ $t('Use judgment when relying on outputs for important business decisions.') }}
          </li>
        </ul>
        <p>
          <strong>{{ $t('Liability & Data') }}</strong>
        </p>
        <ul>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <li v-html="dataProcessingText"></li>
        </ul>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-html="learnMoreText"></p>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-html="consentAcceptanceText"></p>
      </slot>
      <div class="tw-flex tw-justify-center tw-mt-[20px]">
        <button type="button" class="btn btn-primary" @click="handleAiConsent(true)">
          {{ $t('I agree') }}
        </button>
      </div>
    </div>
  </WdsModal>
</template>

<style scoped lang="scss">
.ai-terms-modal {
  :deep(.wds-modal-header) {
    @apply tw-py-6 tw-px-6 !important;
    @apply tw-border-b !important;
    @apply tw-border-gray-200 !important;
  }
}
</style>
